// Command Center, Overview layout (POC). Practical first: a fixed grid of area
// tiles with live prompts and inline actions, an Up next bar that steps through
// the brief, and what changed since the last visit.
// Data and actions: useMorningBrief.js. Toggle: CommandCenterSwitch.jsx.
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlarmClock, AlertTriangle, ArrowRight, Bot, CheckCircle2, ChevronLeft, ChevronRight, Compass, FlaskConical, Inbox, Map, NotebookPen, Plug, Search, Shield, Undo2, X } from 'lucide-react';
import { entityPath } from '../lib/productInsights';
import { BrainStatus } from '../components/brain/BrainUI';
import WorkspaceInsights from '../components/brain/WorkspaceInsights';
import { AREA_OF_TYPE, TYPE_NAMES, born, clock, humanDates, isTyping, safeName, useMorningBrief } from './useMorningBrief';
import './CommandCenterOverview.css';

// Areas sit in a fixed order so their status reads by position.
const AREAS = [
  { id: 'fires', title: 'Fires', icon: Shield, to: '/issues', open: 'Open Firefighter', kinds: ['issue'], empty: 'No critical issues open.' },
  { id: 'decisions', title: 'Decisions', icon: NotebookPen, to: '/decisions', open: 'Open Decisions', kinds: ['decision'], empty: 'No decision reviews due.' },
  { id: 'initiatives', title: 'Initiatives', icon: Map, to: '/roadmap', open: 'Open Roadmap', kinds: ['readiness'], empty: 'Every Now initiative has what it needs.' },
  { id: 'discovery', title: 'Discovery', icon: Search, to: '/discovery', open: 'Open Discovery', kinds: ['validated', 'stronger', 'stale'], empty: 'No problems need attention.' },
  { id: 'learning', title: 'Learning', icon: FlaskConical, to: '/learning', open: 'Open Learning', kinds: ['experiment'], empty: 'No experiments due for review.' },
];
const INBOX_AREA = { id: 'inbox', title: 'Inbox', icon: Inbox, to: '/inbox', open: 'Open Inbox', empty: 'No unprocessed signals.' };
const KINDS = {
  issue: { type: 'issues', label: 'Critical issue' },
  readiness: { type: 'initiatives', label: 'Initiative' },
  validated: { type: 'problems', label: 'Problem' },
  stronger: { type: 'problems', label: 'Problem' },
  stale: { type: 'problems', label: 'Stale evidence' },
  decision: { type: 'decisions', label: 'Decision review' },
  experiment: { type: 'experiments', label: 'Experiment' },
};
const AREA_OF = Object.fromEntries(AREAS.flatMap(a => a.kinds.map(k => [k, a])));
const ROW_KEYS = 'J K Enter S E Z';
const stripSelector = id => `.ov-main [data-strip="${window.CSS?.escape ? window.CSS.escape(id) : String(id).replace(/["\\]/g, '\\$&')}"]`;
const untilText = iso => new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });

function Row({ s, i = 0, current, busy, onFocus, onAct, onSnooze, onDismiss, onUndo }) {
  const { item, kind, meta, status, isNew } = s;
  if (status.stamped) return <li className="ov-row is-dismissed" role="status">
    <span className="ov-row-main"><span className="ov-row-title">Dismissed: {item.title}</span></span>
    <button type="button" className="ov-text-btn" onClick={onUndo} disabled={!status.recordId}><Undo2 aria-hidden="true" />Undo</button>
  </li>;
  return <li tabIndex={-1} data-strip={item.id} aria-label={`${kind.label}: ${item.title}`} aria-keyshortcuts={ROW_KEYS} aria-current={current ? 'true' : undefined}
    className={`ov-row${current ? ' is-current' : ''}`} data-urgency={s.urgency} style={{ viewTransitionName: safeName(item.id), '--i': i }} onFocus={onFocus}>
    <Link className="ov-row-main" to={item.href} onClick={onAct}>
      <span className="ov-row-title">{isNew && <span className="ov-new" title="Not in your previous brief"><span className="v2-sr">New to brief: </span></span>}{item.title}</span>
      <span className="ov-row-detail">{meta && <span className="ov-row-meta">{meta}</span>}{humanDates(item.detail)}</span>
    </Link>
    <span className="ov-row-actions">
      <button type="button" className="ov-icon-btn" onClick={onSnooze} disabled={busy} aria-label={`Snooze "${item.title}" for a day`} title="Snooze 1 day (S)"><AlarmClock aria-hidden="true" /></button>
      <button type="button" className="ov-icon-btn" onClick={onDismiss} disabled={busy} aria-label={`Dismiss "${item.title}"`} title="Dismiss (E)"><X aria-hidden="true" /></button>
    </span>
  </li>;
}

// A snoozed prompt stays in its own tile, so the tile never claims it is clear.
function SnoozedRow({ s, busy, onWake }) {
  return <li className="ov-row is-snoozed" style={{ viewTransitionName: safeName(s.item.id) }}>
    <span className="ov-row-main">
      <span className="ov-row-title"><AlarmClock aria-hidden="true" className="ov-snooze-mark" />{s.item.title}</span>
      <span className="ov-row-detail">Snoozed until {untilText(s.status.snoozedUntil)}</span>
    </span>
    <button type="button" className="ov-text-btn" onClick={onWake} disabled={busy}>Wake now</button>
  </li>;
}

// A count that pops when it changes, so updates are noticed without a reload.
function Count({ value, loading, label, urgency }) {
  const prev = useRef(value);
  const [bump, setBump] = useState(0);
  useEffect(() => { if (!loading && prev.current !== value) setBump(b => b + 1); prev.current = value; }, [value, loading]);
  return <span key={bump} className={`ov-count${value ? '' : ' is-zero'}${bump ? ' is-bump' : ''}`} data-urgency={value ? urgency : undefined} aria-label={label}>{loading ? '—' : value}</span>;
}

// Each tile is a fixed window: about four and a half rows, then it scrolls inside.
function Tile({ area, items, snoozed = [], loading, children, renderSnoozed, count, note }) {
  // A tile's count takes the color of its most urgent live item, and only then.
  const live = items.filter(r => r.status && !r.status.stamped);
  const urgency = live.some(r => r.urgency === 'critical') ? 'critical' : live.some(r => r.urgency === 'attention') ? 'attention' : 'normal';
  const overdue = area.id === 'fires' ? 0 : live.filter(r => r.urgency === 'critical').length;
  const Icon = area.icon;
  return <section id={`ov-${area.id}`} className={`ov-tile area-${area.id}`} aria-labelledby={`ov-h-${area.id}`}>
    <header className="ov-tile-head">
      <span className="ov-tile-icon" aria-hidden="true"><Icon /></span>
      <h2 id={`ov-h-${area.id}`}>{area.title}</h2>
      {area.id === 'fires' && count > 0 && <span className="ov-live-dot" title="Open critical issues" aria-hidden="true" />}
      <Count value={count} loading={loading} urgency={urgency} label={`${loading ? 'Loading' : count} ${area.title.toLowerCase()} items`} />
      {overdue > 0 && <span className="ov-urgent"><AlertTriangle aria-hidden="true" />{overdue} overdue</span>}
      <Link className="ov-tile-link" to={area.to}>{area.open}<ArrowRight aria-hidden="true" /></Link>
    </header>
    {loading ? <div className="ov-skeleton" aria-hidden="true"><span /><span /></div> : <>
      {!items.length && !snoozed.length && <p className="ov-clear"><CheckCircle2 aria-hidden="true" />{area.empty}</p>}
      {!count && snoozed.length > 0 && <p className="ov-clear is-quiet">Nothing active. {snoozed.length} snoozed below.</p>}
      {(items.length > 0 || snoozed.length > 0) && <div className="ov-scroll">
        {items.length > 0 && <ul className="ov-rows">{children(items)}</ul>}
        {snoozed.length > 0 && <ul className="ov-rows">{snoozed.map(renderSnoozed)}</ul>}
      </div>}
      {note && <p className="ov-tile-note">{note}</p>}
    </>}
  </section>;
}

export default function CommandCenterOverview({ layoutSwitch = null }) {
  const b = useMorningBrief({ kinds: KINDS, movingClass: 'ov-moving' });
  const { data, loading, workspace, guide, now, sinceText, error, busy, strips, fires, today, watch, clearedToday, arrivals, record, remove, onKeyDown } = b;
  const navigate = useNavigate();
  const [currentId, setCurrentId] = useState(null);
  const [notice, setNotice] = useState(null); // the Up next bar's own undo window
  const lastIndex = useRef(0);
  const mainRef = useRef(null);
  const rootRef = useRef(null);
  const [dir, setDir] = useState('next');
  const [entering, setEntering] = useState(true);
  const [syncAt, setSyncAt] = useState(() => new Date());
  useEffect(() => { if (!loading) { const t = setTimeout(() => setEntering(false), 900); return () => clearTimeout(t); } return undefined; }, [loading]);
  // Every refresh of the workspace data is shown as a live sync.
  useEffect(() => { if (!loading) setSyncAt(new Date()); }, [data, loading]);
  // Keyboard-driven changes never animate; pointer-driven ones may.
  useEffect(() => {
    const mark = value => () => { if (rootRef.current) rootRef.current.dataset.input = value; };
    const keys = mark('keys'); const pointer = mark('pointer');
    document.addEventListener('keydown', keys, true); document.addEventListener('pointerdown', pointer, true);
    return () => { document.removeEventListener('keydown', keys, true); document.removeEventListener('pointerdown', pointer, true); };
  }, []);

  const active = [...fires, ...today, ...watch.filter(s => !s.status.snoozedUntil)];
  const snoozed = watch.filter(s => s.status.snoozedUntil);
  const queue = active.filter(s => !s.status.stamped);
  // One cursor for the bar, the stepper and keyboard focus. When the current
  // item leaves the queue, the cursor stays in place and lands on the next one.
  const found = queue.findIndex(s => s.item.id === currentId);
  const index = found >= 0 ? found : Math.min(lastIndex.current, Math.max(0, queue.length - 1));
  lastIndex.current = index;
  const next = queue[index];
  const inbox = data.inbox.filter(i => i.status !== 'processed').sort((x, y) => new Date(born(y) || 0) - new Date(born(x) || 0));
  const byId = Object.fromEntries(strips.map(s => [s.item.id, s]));

  useEffect(() => { if (!notice) return undefined; const t = setTimeout(() => setNotice(null), 5000); return () => clearTimeout(t); }, [notice]);

  const focusRow = id => document.querySelector(stripSelector(id))?.focus();
  const step = delta => { const target = queue[Math.min(queue.length - 1, Math.max(0, index + delta))]; if (target) { setDir(delta < 0 ? 'prev' : 'next'); setCurrentId(target.item.id); } };

  // Keep the Up next item visible inside its tile's scrolling window.
  const nextId = next?.item.id;
  useEffect(() => {
    if (!nextId) return;
    const row = document.querySelector(stripSelector(nextId));
    const box = row?.closest('.ov-scroll');
    if (!box) return;
    const top = row.offsetTop - box.offsetTop;
    if (top < box.scrollTop) box.scrollTop = top;
    else if (top + row.offsetHeight > box.scrollTop + box.clientHeight) box.scrollTop = top + row.offsetHeight - box.clientHeight;
  }, [nextId]);

  // J/K work as soon as the page opens, not only after a row has focus.
  useEffect(() => {
    const onDocKey = e => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (mainRef.current?.contains(e.target)) return;
      if (!['j', 'k'].includes(e.key.toLowerCase()) || !next) return;
      e.preventDefault();
      focusRow(next.item.id);
    };
    document.addEventListener('keydown', onDocKey);
    return () => document.removeEventListener('keydown', onDocKey);
  });

  const handlers = s => ({
    busy: busy.has(s.item.id),
    current: next?.item.id === s.item.id,
    onFocus: () => setCurrentId(s.item.id),
    onAct: () => guide?.act(s.item.id),
    onSnooze: () => record(s.item, 'snooze'),
    onDismiss: () => record(s.item, 'dismiss'),
    onUndo: () => remove(s.item, [s.status.recordId]),
    onWake: () => remove(s.item, s.status.records.map(f => f.id)),
  });
  const actFromBar = (s, action) => { setNotice({ id: s.item.id, action, title: s.item.title }); record(s.item, action, false); };
  const undoFromBar = () => {
    const s = byId[notice.id];
    if (s?.status.stamped && s.status.recordId) remove(s.item, [s.status.recordId], false);
    else if (s?.status.snoozedUntil) remove(s.item, s.status.records.map(f => f.id), false);
    setCurrentId(notice.id);
    setNotice(null);
  };
  const noticeItem = notice && byId[notice.id];
  const canUndo = noticeItem && ((noticeItem.status.stamped && noticeItem.status.recordId) || (noticeItem.status.snoozedUntil && noticeItem.status.records?.every(f => !String(f.id).startsWith('local:'))));
  const noticeLine = notice && <div className="ov-next-notice" role="status">
    <span title={notice.title}>{notice.action === 'dismiss' ? 'Dismissed' : 'Snoozed'}: <strong>{notice.title}</strong></span>
    <button type="button" className="ov-text-btn" onClick={undoFromBar} disabled={!canUndo}><Undo2 aria-hidden="true" />Undo</button>
  </div>;
  const onMainKey = e => {
    // Enter on an inbox row opens Inbox; brief rows are handled by the shared engine.
    const row = e.target.closest?.('[data-inbox]');
    if (row && e.key === 'Enter' && row === e.target) { e.preventDefault(); navigate('/inbox'); return; }
    onKeyDown(e);
  };
  const byArea = id => active.filter(s => AREA_OF[s.kind.prefix]?.id === id);
  const snoozedIn = id => snoozed.filter(s => AREA_OF[s.kind.prefix]?.id === id);
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const nextArea = next && AREA_OF[next.kind.prefix];
  const NextIcon = nextArea?.icon;
  const toSince = () => document.getElementById('ov-since')?.scrollIntoView({ block: 'start', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });

  return <div ref={rootRef} className={`v2-page ov${entering ? ' is-entering' : ''}`}>
    <header className="ov-head">
      <div>
        <h1>Your morning brief</h1>
        <p>{[workspace?.name, now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })].filter(Boolean).join(' · ')}
          {!loading && <> · <button type="button" className="ov-link-btn" onClick={toSince}>{plural(arrivals.length, 'change', 'changes')} {sinceText ? `since ${sinceText.split(' ')[0]}` : 'recently'}</button></>}
          {!loading && clearedToday > 0 && ` · ${clearedToday} cleared today`}
</p>
      </div>
      <div className="ov-head-tools">{!loading && <span className="ov-sync" role="status" title="Updates as your records change"><span key={syncAt.getTime()} className="ov-sync-dot" aria-hidden="true" />Live · {syncAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>}{layoutSwitch}<Link className="v2-button" to="/inbox" data-guide="capture-signal"><Inbox aria-hidden="true" />Capture a signal</Link></div>
    </header>
    <BrainStatus quietRefresh />
    {error && <div className="v2-error ov-error" role="alert">{error}</div>}

    <div className="ov-layout">
      <div className="ov-main" ref={mainRef} onKeyDown={onMainKey}>
        <section className="ov-next area-next" aria-label="Up next" data-guide={next ? 'brief-first' : undefined} data-dir={dir}>
          {next && <div className="ov-next-band" aria-hidden="true">{NextIcon && <NextIcon />}<span>Up next</span>{nextArea && <span className={`ov-next-band-area area-${nextArea.id}`}>{nextArea.title}</span>}</div>}
          {next && queue.length > 1 && <span className="ov-progress" style={{ transform: `scaleX(${(index + 1) / queue.length})` }} aria-hidden="true" />}
          {loading ? <div className="ov-skeleton" aria-hidden="true"><span /><span /></div>
            : next ? <>
              <div className="ov-next-body" key={next.item.id}>
                <h2>{next.item.title}</h2>
                <p className="ov-next-detail">{humanDates(next.item.detail)}</p>
                <p className="ov-next-tags"><span className="ov-chip" data-urgency={next.urgency}>{next.kind.label}</span>{next.meta && <span className="ov-next-meta" data-urgency={next.urgency}>{next.meta}</span>}{next.isNew && <span className="ov-new-text"><span className="ov-new" aria-hidden="true" />New to brief</span>}</p>
              </div>
              <div className="ov-next-side">
                <div className="ov-next-actions">
                  <Link className="v2-button" to={next.item.href} onClick={() => guide?.act(next.item.id)} key={`go-${next.item.id}`}>{next.item.action}<ArrowRight aria-hidden="true" /></Link>
                  <button type="button" className="v2-button secondary" onClick={() => actFromBar(next, 'snooze')} disabled={busy.has(next.item.id)}><AlarmClock aria-hidden="true" />Snooze</button>
                  <button type="button" className="v2-button secondary" onClick={() => actFromBar(next, 'dismiss')} disabled={busy.has(next.item.id)}><X aria-hidden="true" />Dismiss</button>
                </div>
                {/* The undo line takes the stepper's place, so the bar never changes height. */}
                {notice ? noticeLine : <div className="ov-stepper" aria-label="Step through the brief">
                  <button type="button" className="ov-step-btn" onClick={() => step(-1)} disabled={index === 0}><ChevronLeft aria-hidden="true" />Previous</button>
                  <span aria-live="polite"><strong>{index + 1}</strong> of {queue.length}</span>
                  <button type="button" className="ov-step-btn" onClick={() => step(1)} disabled={index >= queue.length - 1}>Next<ChevronRight aria-hidden="true" /></button>
                </div>}
              </div>
            </>
              : <>{notice && noticeLine}<p className="ov-clear ov-next-clear"><CheckCircle2 aria-hidden="true" />{data.problems.length ? 'You are clear. Nothing in your records needs a decision right now.' : 'Start with a customer problem: capture a signal or add a problem in Discovery.'}</p></>}
        </section>

        <div className="ov-grid">
          {AREAS.map(area => {
            const items = byArea(area.id);
            return <Tile key={area.id} area={area} items={items} snoozed={snoozedIn(area.id)} count={items.filter(s => !s.status.stamped).length} loading={loading}
              renderSnoozed={s => <SnoozedRow key={s.item.id} s={s} {...handlers(s)} />}>
              {shown => shown.map((s, i) => <Row key={s.item.id} s={s} i={i} {...handlers(s)} />)}
            </Tile>;
          })}
          <Tile area={INBOX_AREA} items={inbox} count={inbox.length} loading={loading} note={inbox.length ? 'Signals are reviewed and filed in Inbox.' : null}>
            {shown => shown.map(i => <li key={i.id} tabIndex={-1} data-strip={`inbox:${i.id}`} data-inbox="" aria-label={`Signal: ${i.title || 'Untitled signal'}`} aria-keyshortcuts="J K Enter" className="ov-row">
              <Link className="ov-row-main" to="/inbox"><span className="ov-row-title">{i.title || i.content?.slice(0, 90) || 'Untitled signal'}</span><span className="ov-row-detail">{born(i) && <span className="ov-row-meta">{clock(born(i), now)}</span>}Unprocessed signal</span></Link>
            </li>)}
          </Tile>
        </div>
        <p className="ov-keys" aria-hidden="true"><kbd>J</kbd><kbd>K</kbd> move between items · <kbd>Enter</kbd> open · <kbd>S</kbd> snooze · <kbd>E</kbd> dismiss · <kbd>Z</kbd> undo</p>
      </div>

      <aside className="ov-side">
        <section id="ov-since" className="ov-since" aria-labelledby="ov-since-title">
          <h2 id="ov-since-title">{sinceText ? `Since ${sinceText}` : 'Recently updated'}</h2>
          {arrivals.length
            ? <ol className="ov-since-list">{arrivals.map((n, i) => <li key={`${n.type}:${n.id}`} className={`area-${AREA_OF_TYPE[n.type] || 'next'}`} style={{ '--i': i }}>
              <Link to={entityPath(n.type, n.id)}><i aria-hidden="true" /><span><strong>{n.label}</strong><small>{n.change || `Updated ${TYPE_NAMES[n.type] || n.type}`}</small></span><time dateTime={n.at}>{clock(n.at, now)}</time></Link>
            </li>)}</ol>
            : <p className="v2-muted">{loading ? 'Loading changes…' : 'No records changed since then.'}</p>}
        </section>
        <nav className="ov-shortcuts" aria-label="Shortcuts">
          {guide?.activation?.path === 'jira' && !data.imports.length && <Link to="/onboarding"><Plug aria-hidden="true" />Setup &amp; integrations</Link>}
          <Link to="/autopilot"><Bot aria-hidden="true" />Agent team</Link>
          <Link to="/brain"><Compass aria-hidden="true" />Product Brain</Link>
          <Link to="/decisions"><NotebookPen aria-hidden="true" />Record a decision</Link>
          <Link to="/learning"><CheckCircle2 aria-hidden="true" />Review learning</Link>
        </nav>
        <WorkspaceInsights area="command_center" />
      </aside>
    </div>
  </div>;
}
